import Swal from "sweetalert2";
import ApiService, { HttpMethod } from "../../../commons/api/api_service";
import BaseResponse from "../../../commons/api/base_response";
import { Endpoints } from "../../../commons/api/endpoints";
import { ProviderVM } from "../../../commons/base/providers/view_model";
import {
    ShowErrorViewModel,
    ShowSnackbarModel,
} from "../../../commons/scaffold/scaffold";
import OutletModel from "../../models/outlet_model";
import FormOpsModel from "../models/form_ops_model";
import { OutletType } from "../../models/outlet_type_enum";

export default class FormOpsReminderListViewModel extends ProviderVM {
    isLoading: boolean = false;
    errorModel?: ShowErrorViewModel;
    snackbarModel?: ShowSnackbarModel;
    outlets: OutletModel[] = [];
    formsList: FormOpsModel[] = [];
    searchQuery: string = "";

    // for send once
    selectedFormId: number = 0;
    isShowingModal: boolean = false;
    selectedOutletsMap: Map<number, OutletModel> = new Map<
        number,
        OutletModel
    >();
    outletType: OutletType = OutletType.CUSTOM;

    constructor() {
        super();
        this.fetchPreloading();
        this.fetchFormsList();
    }

    get filteredForms() {
        return this.formsList.filter((form) =>
            form.title.toLowerCase().includes(this.searchQuery.toLowerCase())
        );
    }

    get outletsInOptions() {
        const selectedOutletIds = [...this.selectedOutletsMap.keys()];
        return this.outlets.filter(
            (outlet) => !selectedOutletIds.includes(outlet.id)
        );
    }

    fetchPreloading() {
        this.emit(() => {
            this.isLoading = true;
        });

        ApiService.fetch(Endpoints.formOpsReminderPreloading, {
            httpMethod: HttpMethod.get,
            onSuccess: (
                response: BaseResponse<{
                    outlets: OutletModel[];
                }>
            ) => {
                if (response.code === 200) {
                    const outletsList = response.data?.outlets ?? [];

                    this.emit(() => {
                        this.isLoading = false;
                        this.outlets = outletsList;
                    });
                } else {
                    this.emit(() => {
                        this.isLoading = false;
                        this.errorModel = {
                            action: () => {},
                            message: response.message,
                        };
                    });
                }
            },
            onFailure: (error) => {
                this.emit(() => {
                    this.isLoading = false;
                    this.errorModel = {
                        action: () => {},
                        message: `${error}`,
                    };
                });
            },
        });
    }

    fetchFormsList() {
        ApiService.fetch(Endpoints.formOpsReminderGetList, {
            httpMethod: HttpMethod.get,
            onSuccess: (
                response: BaseResponse<{
                    forms: FormOpsModel[];
                }>
            ) => {
                if (response.code === 200) {
                    const forms = response.data?.forms ?? [];

                    this.emit(() => {
                        this.isLoading = false;
                        this.formsList = forms;
                    });
                } else {
                    this.errorModel = {
                        action: () => {},
                        message: response.message,
                    };
                }
            },
            onFailure: (error) => {
                this.emit(() => {
                    this.isLoading = false;
                    this.errorModel = {
                        action: () => {},
                        message: `${error}`,
                    };
                });
            },
        });
    }

    updateSearchQuery(value: string) {
        this.emit(() => {
            this.searchQuery = value;
        });
    }

    toggleOutlet(outletModel: OutletModel) {
        const updatedMap = new Map(this.selectedOutletsMap);

        if (updatedMap.has(outletModel.id)) {
            updatedMap.delete(outletModel.id);
        } else {
            updatedMap.set(outletModel.id, outletModel);
        }

        this.emit(() => {
            this.selectedOutletsMap = updatedMap;
        });
    }

    openModalForSendOnce(formId: number) {
        this.emit(() => {
            this.isShowingModal = true;
            this.selectedFormId = formId;
        });
    }

    updateOutletType(value: OutletType) {
        this.emit(() => {
            this.outletType = value;
        });
    }

    closeModal() {
        this.emit(() => {
            this.isShowingModal = false;
            this.selectedFormId = 0;
            this.outletType = OutletType.CUSTOM;
            this.selectedOutletsMap = new Map<number, OutletModel>();
        });
    }

    sendOnce(args: { onSuccess: () => void }) {
        Swal.fire({
            title: "Konfirmasi",
            text: "Apakah sudah yakin?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Ya",
        }).then((result) => {
            if (result.isConfirmed) {
                this.emit(() => {
                    this.isLoading = true;
                });

                ApiService.fetch<
                    { outletIds: number[]; outletType: OutletType },
                    BaseResponse<{}>
                >(
                    `${Endpoints.formOpsReminderSendOnce}/${this.selectedFormId}` as Endpoints,
                    {
                        httpMethod: HttpMethod.post,
                        parameters: {
                            outletIds: [...this.selectedOutletsMap.keys()],
                            outletType: this.outletType,
                        },
                        onSuccess: (response: BaseResponse<{}>) => {
                            if (response.code === 200) {
                                this.emit(() => {
                                    this.isLoading = false;
                                    this.selectedOutletsMap = new Map<
                                        number,
                                        OutletModel
                                    >();
                                    this.outletType = OutletType.CUSTOM;
                                    this.snackbarModel = {
                                        key: Date.now().toString(),
                                        type: "normal",
                                        message: response.message,
                                    };
                                });

                                args.onSuccess();
                            } else {
                                this.errorModel = {
                                    action: () => {},
                                    message: response.message,
                                };
                            }
                        },
                        onFailure: (error) => {
                            this.emit(() => {
                                this.isLoading = false;
                                this.errorModel = {
                                    action: () => {},
                                    message: `${error}`,
                                };
                            });
                        },
                    }
                );
            }
        });
    }

    deleteForm(args: { formId: number; onSuccess: () => void }) {
        Swal.fire({
            title: "Konfirmasi",
            text: "Apaka sudah yakin?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Ya",
        }).then((result) => {
            if (result.isConfirmed) {
                this.emit(() => {
                    this.isLoading = true;
                });

                ApiService.fetch<{ formId: number }, BaseResponse<{}>>(
                    `${Endpoints.formOpsReminderSoftDelete}/${args.formId}` as Endpoints,
                    {
                        httpMethod: HttpMethod.delete,
                        onSuccess: (response: BaseResponse<{}>) => {
                            if (response.code === 200) {
                                this.emit(() => {
                                    this.isLoading = false;
                                    this.snackbarModel = {
                                        key: Date.now().toString(),
                                        type: "normal",
                                        message: response.message,
                                    };
                                });

                                args.onSuccess();
                            } else {
                                this.errorModel = {
                                    action: () => {},
                                    message: response.message,
                                };
                            }
                        },
                        onFailure: (error) => {
                            this.emit(() => {
                                this.isLoading = false;
                                this.errorModel = {
                                    action: () => {},
                                    message: `${error}`,
                                };
                            });
                        },
                    }
                );
            }
        });
    }
}
